/**
 * Maximum number of results to fetch from WordPress API in a single request
 * This limit affects pagination and total posts displayed
 * Set to 10 for development/testing, increase for production to handle larger post volumes
 * @type {number}
 */
export const API_MAX_RESULTS = 100;
export const POSTS_PER_PAGE = 7;

/**
 * Email address where contact form submissions will be sent
 */
export const SOCIAL_LINKS = {
  discord: "https://discord.gg/maven",
  instagram: "https://www.instagram.com/maven.trading",
  youtube: "https://www.youtube.com/@maventrading",
  tiktok: "https://www.tiktok.com/@maven.trading",
  facebook: "https://www.facebook.com/themaventrading",
  linkedin: "https://www.linkedin.com/company/mavenproptrading/",
  twitter: "https://x.com/Maventrading",
};

export const MAVEN_ADDRESS = {
  street: "DSO-IFZA, IFZA Properties",
  city: "Silicon Oasis, Dubai,",
  postalCode: "0000000000",
};

/**
 * Supported languages for the application
 */
export const SUPPORTED_LANGUAGES = ["en", "es", "pt"] as const;

/**
 * Type for supported languages
 */
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Routes that require payment scripts to be loaded
 */
export const CHECKOUT_ROUTES = [
  "/checkout",
  "/es/checkout",
  "/pt/checkout",
] as const;

/**
 * Utility function to check if the current pathname is a checkout page
 * This is more maintainable than using regex patterns
 * @param pathname - The current URL pathname
 * @returns boolean indicating if this is a checkout page
 */
export const is_checkout_page = (pathname: string): boolean => {
  // Normalize pathname by removing trailing slash and converting to lowercase
  const normalized_path = pathname.toLowerCase().replace(/\/$/, "");

  return CHECKOUT_ROUTES.some((route) => {
    const normalized_route = route.toLowerCase();
    return normalized_path === normalized_route;
  });
};

export const COOKIE_NAMES = {
  ORDER_ID: "maven_order_id",
  PROP_FIRM_MATCH_CLICK_ID: "pfm_click_id",
  TAPFILIATE_CLICK_ID: "tapfiliate_click_id",
};

/**
 * This list will be used in all payment plugins to exclude these countries from the checkout process.
 * For the up-to-date list of banned countries, see the Notion page:
 * "Banned Countries for Payments" – https://www.notion.so/Banned-Countries-for-Payments-286fd00da97b8045a7bdd76be84c500c
 * (Ensure you have access to the Notion workspace. If not, contact the admin for access.)
 */
export const COMPLETELY_BANNED_COUNTRIES = [
  "AF",
  // "AE",
  "BY",
  "BI",
  "CF",
  "CG",
  "CD",
  "ER",
  "GN",
  "GW",
  "HT",
  "IR",
  "ML",
  "MD",
  "KP",
  "PS",
  "PG",
  "RU",
  "LC",
  "SL",
  "SO",
  "SS",
  "SD",
  "TN",
  "VE",
  "YE",
];

export const is_experimental_feature_enabled = () => {
  if (typeof window !== "undefined") {
    const url_params = new URLSearchParams(window.location.search);
    return url_params.get("experimental") === "true";
  }

  return false;
};

export const AFRICA_BANNED_COUNTRIES = [
  "DZ",
  "AO",
  "BJ",
  "BW",
  "BF",
  "BI",
  "CV",
  "CM",
  "CF",
  "TD",
  "KM",
  "CG",
  "CD",
  "DJ",
  "GQ",
  "ER",
  "SZ",
  "ET",
  "EG",
  "GA",
  "GM",
  "GH",
  "GN",
  "GW",
  "CI",
  "KE",
  "LS",
  "LR",
  "LY",
  "MG",
  "MW",
  "ML",
  "MR",
  "MU",
  "YT",
  "MA",
  "MZ",
  "NA",
  "NE",
  "NG",
  "RE",
  "RW",
  "SH",
  "ST",
  "SN",
  "SC",
  "SL",
  "SO",
  "SS",
  "SD",
  "TZ",
  "TG",
  "TN",
  "UG",
  "EH",
  "ZM",
  "ZW",
  "CY",
  "ZA",
];

/**
 * List of countries in North America and South America where Stripe should be available.
 * This intentionally excludes any countries listed in COMPLETELY_BANNED_COUNTRIES.
 * Notes:
 * - Caribbean and Central American territories included under North America.
 * - This is used to explicitly whitelist Stripe to the Americas only.
 */
// Explicit whitelist of countries in the Americas where Stripe is enabled
export const STRIPE_AMERICAS_ALLOWED_COUNTRIES = [
  // North America (including Central America & Caribbean)
  "US", // United States
  "CA", // Canada
  "MX", // Mexico
  "AG", // Antigua and Barbuda
  "AI", // Anguilla
  "AW", // Aruba
  "BB", // Barbados
  "BZ", // Belize
  "BM", // Bermuda
  "BS", // Bahamas
  "CR", // Costa Rica
  // "CU", // Cuba (excluded: COMPLETELY_BANNED_COUNTRIES)
  "CW", // Curaçao
  "DM", // Dominica
  "DO", // Dominican Republic
  "GD", // Grenada
  "GL", // Greenland
  "GT", // Guatemala
  "HN", // Honduras
  // "HT", // Haiti (excluded: COMPLETELY_BANNED_COUNTRIES)
  "JM", // Jamaica
  "KN", // Saint Kitts and Nevis
  "KY", // Cayman Islands
  // "LC", // Saint Lucia (excluded: COMPLETELY_BANNED_COUNTRIES)
  "MS", // Montserrat
  // "NI", // Nicaragua (excluded: COMPLETELY_BANNED_COUNTRIES)
  "PA", // Panama
  "PR", // Puerto Rico
  "SV", // El Salvador
  "SX", // Sint Maarten
  "TC", // Turks and Caicos Islands
  "TT", // Trinidad and Tobago
  "VC", // Saint Vincent and the Grenadines
  "VG", // British Virgin Islands
  "VI", // U.S. Virgin Islands
  "GP", // Guadeloupe
  "MQ", // Martinique
  "BL", // Saint Barthélemy
  "MF", // Saint Martin (French part)

  // South America
  "AR", // Argentina
  "BO", // Bolivia
  "BR", // Brazil
  "CL", // Chile
  "CO", // Colombia
  "EC", // Ecuador
  "FK", // Falkland Islands
  "GF", // French Guiana
  "GY", // Guyana
  "PE", // Peru
  "PY", // Paraguay
  "SR", // Suriname
  "UY", // Uruguay
  // "VE", // Venezuela (excluded: COMPLETELY_BANNED_COUNTRIES)
];

/**
 * Business logic constants for challenge plan categorization
 * Standard plans include multi-step challenges, while others are single-step
 */
export const PLAN_TYPES = {
  STANDARD_PLANS: ["1 Step", "2 Step", "3 Step"] as string[],
} as const;
